import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Music, Heart, Award, Calendar, MapPin, 
  ChevronRight, Star, Drum, Globe, BookOpen, Mail
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditableImage } from '@/components/edit/PageEditableImage';
import { EditableLink } from '@/components/edit/EditableLink';
import { EditableList } from '@/components/edit/EditableList';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fade in animation
const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
  >
    {children}
  </motion.div>
);

const getDefaultContent = () => ({
  hero: {
    title: "Escola de Samba Casa Samba",
    subtitle: "New Orleans' Premier Afro-Brazilian Cultural Organization",
    description: "Founded in 1987, Escola de Samba Casa Samba is dedicated to preserving and sharing the rich traditions of Afro-Brazilian music, dance, and culture with the New Orleans community and beyond.",
    image: "/api/media/casa-samba/library/images/about/hero-zulu-2019.jpeg"
  },
  mission: {
    title: "Our Mission",
    description: "Escola de Samba Casa Samba exists to bridge cultures through the universal language of rhythm."
  },
  history: {
    title: "Our History",
    founded: "1987",
    foundedBy: "Mestre Curtis Pierre",
    description: ""
  },
  leadership: { title: "Escola de Samba Casa Samba Leaders", leaders: [] },
  members: { title: "Key Members of Escola de Samba Casa Samba", description: "", longStandingMembers: [] },
  programs: { title: "What We Do", items: [] },
  contact: { title: "Connect With Us", email: "therealsambaman@yahoo.com", location: "New Orleans, Louisiana" }
});

export function About() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/casa-samba-about`);
        const data = await response.json();
        setContent(data);
      } catch (error) {
        console.error('Error fetching content:', error);
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Scroll to hash target once the content has painted (e.g. /about#members)
  useEffect(() => {
    if (!content || !location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [content, location.hash]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!content) return null;

  return (
    <PageEditProvider slug="about">
      <AboutInner content={content} />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/dashboard#casa-samba-about-editor-btn" label="Edit About Page" />
    </PageEditProvider>
  );
}

function AboutInner({ content }) {
  const IconMap = { Music, BookOpen, Users, Calendar, Award, Heart, Globe, Drum };

  return (
    <div className="min-h-screen bg-background">
      {/* ========== HERO ========== */}
      <section className="relative py-24 bg-gradient-to-br from-primary/25 via-background to-red-900/20 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-yellow-400 to-primary" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <FadeIn>
            <div className="text-center">
              <PageEditable
                path="hero.title"
                as="h1"
                className="text-5xl md:text-7xl font-black text-white mb-4"
                fallback={content.hero?.title || "Escola de Samba Casa Samba"}
              />
              <PageEditable
                path="hero.subtitle"
                as="p"
                className="text-xl md:text-2xl text-white/90 mb-6 max-w-3xl mx-auto"
                fallback={content.hero?.subtitle || ""}
              />
              <PageEditable
                path="hero.description"
                as="p"
                multiline
                className="text-lg text-white/80 max-w-2xl mx-auto mb-8"
                fallback={content.hero?.description || ""}
              />
              <div className="flex flex-wrap justify-center gap-4">
                <EditableLink
                  path="hero.ctaPrimaryLink"
                  fallbackHref="/legacy"
                  fallbackLabel="Meet Our Founder"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-bold rounded-full hover:bg-yellow-400 hover:text-black transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </EditableLink>
                <EditableLink
                  path="hero.ctaSecondaryLink"
                  fallbackHref="/classes"
                  fallbackLabel="View Classes"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-black transition-all"
                />
              </div>
            </div>
          </FadeIn>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* ========== BRIEF HISTORY ========== */}
      <section className="py-20 bg-card/40 border-y border-border/40">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-primary font-bold tracking-[0.3em] text-xs md:text-sm uppercase mb-3 block">
                <PageEditable path="briefHistory.eyebrow" as="span" fallback="Since 1987" />
              </span>
              <PageEditable
                path="briefHistory.title"
                as="h2"
                className="text-4xl md:text-5xl font-black text-foreground"
                fallback="Brief History"
              />
            </div>
          </FadeIn>

          {/* Supporting photo — full/uncropped, sits between the section title and the mission */}
          <FadeIn delay={0.05}>
            <div className="relative mb-14 group">
              <div className="absolute -inset-2 rounded-[1.75rem] bg-gradient-to-br from-primary/60 via-yellow-400/60 to-red-500/60 blur-sm opacity-70" aria-hidden />
              <div className="pointer-events-none absolute -top-3 -left-3 w-8 h-8 border-t-4 border-l-4 border-yellow-400 rounded-tl-2xl z-20" aria-hidden />
              <div className="pointer-events-none absolute -top-3 -right-3 w-8 h-8 border-t-4 border-r-4 border-yellow-400 rounded-tr-2xl z-20" aria-hidden />
              <div className="pointer-events-none absolute -bottom-3 -left-3 w-8 h-8 border-b-4 border-l-4 border-yellow-400 rounded-bl-2xl z-20" aria-hidden />
              <div className="pointer-events-none absolute -bottom-3 -right-3 w-8 h-8 border-b-4 border-r-4 border-yellow-400 rounded-br-2xl z-20" aria-hidden />
              <div className="relative rounded-[1.25rem] overflow-hidden bg-neutral-950 shadow-2xl">
                <PageEditableImage
                  path="briefHistory.image"
                  fallback={content.hero?.image || "/api/media/casa-samba/library/images/about/hero-zulu-2019.jpeg"}
                  alt="Casa Samba performers in full carnival regalia"
                  className="w-full h-auto max-h-[65vh] object-contain block"
                />
              </div>
              <div className="absolute -bottom-3 right-4 md:right-8 bg-yellow-400 rounded-xl px-4 py-2 shadow-xl rotate-[-3deg] z-30">
                <p className="text-xl md:text-2xl font-black text-black leading-none">
                  <PageEditable path="history.founded" fallback={content.history?.founded || "1987"} />
                </p>
                <p className="text-[9px] md:text-[10px] font-bold text-black/80 tracking-widest">EST.</p>
              </div>
            </div>
          </FadeIn>

          {/* Our Mission */}
          <FadeIn delay={0.1}>
            <div className="mb-14">
              <PageEditable
                path="mission.title"
                as="h3"
                className="text-2xl md:text-3xl font-bold text-primary mb-4"
                fallback={content.mission?.title || "Our Mission"}
              />
              <PageEditable
                path="mission.description"
                as="p"
                multiline
                className="text-base md:text-lg text-muted-foreground leading-relaxed"
                fallback={content.mission?.description || "Our mission is to present audiences with an authentic Brasilian Carnival and folk art experience. Casa Samba does this through weekly open rehearsals and frequent school and university workshops. Additionally, we maintain a center that works to educate the New Orleans community and the Gulf South region of the rich cultural traditions of Brasil and the similarities which bridge Carnival in Brasil with Mardi Gras in New Orleans emphasizing the importance of their African tradition and influences."}
              />
            </div>
          </FadeIn>

          {/* Forging Ahead */}
          <FadeIn delay={0.15}>
            <div className="mb-14">
              <PageEditable
                path="forgingAhead.title"
                as="h3"
                className="text-2xl md:text-3xl font-bold text-primary mb-4"
                fallback="Forging Ahead"
              />
              <PageEditable
                path="forgingAhead.description"
                as="p"
                multiline
                className="text-base md:text-lg text-muted-foreground leading-relaxed whitespace-pre-line"
                fallback={"Vision – Casa Samba's vision is to train children and adults in New Orleans and throughout the Gulf South into aspiring practitioners of Brasilian traditional music, dance and cultural arts. Mestre Curtis Pierre has studied over the past 30 years to gain the knowledge and expertise on Brasilian music and dance. Now with the aid of Casa Samba, he would like to give back to the community through offering classes and workshops on the Brasilian music, dance and culture. For the past 32 years he has done hands-on work within his community with hundreds of children. Not only as a performer but as a role model, mentor, big brother and father figure to many of his neighborhood children. Casa Samba and Mestre Curtis have performed for over 1,000,000 children in the metropolitan New Orleans school systems and surrounding parish schools, exposing children to Brasilian culture, music and traditions.\n\nBeing a native of New Orleans and knowing the problems that kids face today, Mestre Curtis has adopted the same philosophy of the samba schools in Brasil in taking a social responsibility for safeguarding our children and providing them with creative and challenging opportunities and experiences other than what is found in the streets.\n\nIt is our desire to provide a venue for getting the community, specifically children and young adults, interested in music and dance and to provide them with a place to participate in supervised and structured activities like residencies, after-school programs, and during the summer months."}
              />
            </div>
          </FadeIn>

          {/* Founding */}
          <FadeIn delay={0.2}>
            <div className="mb-10 bg-background border border-primary/20 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-7 h-7 text-black" />
                </div>
                <div>
                  <p className="text-xs font-bold text-primary tracking-widest">FOUNDED BY</p>
                  <PageEditable
                    path="history.foundedBy"
                    as="p"
                    className="text-lg font-bold text-foreground"
                    fallback={content.history?.foundedBy || "Mestre Curtis Pierre"}
                  />
                </div>
              </div>
              <PageEditable
                path="history.founding"
                as="p"
                multiline
                className="text-base md:text-lg text-muted-foreground leading-relaxed"
                fallback={"Casa Samba was founded in 1987 by Mestre Curtis Pierre, Bill Lennon, Chuk and Carolyn Barber. On July 21, 1993, Mayor Sidney Bartholomew proclaimed Casa Samba as the first samba school in New Orleans, one of the premier cities of Carnival in the world. The group is directed by Mestre Curtis Pierre and Brasilian master drummer Jorge \"Alabe\" Bezerra."}
              />
            </div>
          </FadeIn>

          {/* Performances */}
          <FadeIn delay={0.25}>
            <div>
              <PageEditable
                path="history.performances"
                as="p"
                multiline
                className="text-base md:text-lg text-muted-foreground leading-relaxed"
                fallback={"Casa Samba's performances combine traditional music, vocals, theater, dance and drumming to capture the spirit of Carnival in Brasil and Afro-Brasilian traditions and history. We showcase our performances at major hotels, festivals, conventions and special events and through our annual participation in several Carnaval/Mardi Gras parades and coronations (such as Endymion and Zulu balls) and at the New Orleans Jazz and Heritage Festival. Appropriately enough, Casa Samba first emerged in 1986 during Carnaval/Mardi Gras."}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ========== WHAT WE DO ========== */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <PageEditable path="programs.eyebrow" as="span" className="text-primary font-bold tracking-wider text-sm mb-4 block" fallback="OUR PROGRAMS" />
              <PageEditable
                path="programs.title"
                as="h2"
                className="text-3xl md:text-4xl font-bold text-foreground"
                fallback={content.programs?.title || "What We Do"}
              />
            </div>
          </FadeIn>

          <EditableList
            path="programs.items"
            fallback={content.programs?.items || []}
            emptyItem={() => ({ title: 'New Program', description: '', icon: 'Music' })}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            renderItem={(program, index) => {
              const Icon = IconMap[program.icon] || Music;
              return (
                <FadeIn key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-card rounded-2xl p-6 shadow-lg border border-border/50 hover:border-primary/40 transition-all"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-primary to-yellow-600 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <PageEditable
                      path={`programs.items.${index}.title`}
                      as="h3"
                      className="text-xl font-bold text-foreground mb-2"
                      fallback={program.title}
                    />
                    <PageEditable
                      path={`programs.items.${index}.description`}
                      as="p"
                      multiline
                      className="text-muted-foreground"
                      fallback={program.description}
                    />
                  </motion.div>
                </FadeIn>
              );
            }}
          />
        </div>
      </section>

      {/* ========== LEADERSHIP ========== */}
      {content.leadership?.leaders?.length > 0 && (
        <section className="py-20 bg-card/40 border-y border-border/40">
          <div className="max-w-6xl mx-auto px-6">
            <FadeIn>
              <div className="text-center mb-16">
                <PageEditable path="leadership.eyebrow" as="span" className="text-primary font-bold tracking-wider text-sm mb-4 block" fallback="MEET THE TEAM" />
                <PageEditable
                  path="leadership.title"
                  as="h2"
                  className="text-3xl md:text-4xl font-bold text-foreground"
                  fallback={content.leadership?.title || "Our Leadership"}
                />
              </div>
            </FadeIn>

          <EditableList
            path="leadership.leaders"
            fallback={content.leadership.leaders}
            emptyItem={() => ({ name: 'New Leader', role: '', bio: '', image: '' })}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            renderItem={(leader, index) => (
                <FadeIn key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-card rounded-2xl overflow-hidden shadow-lg"
                  >
                    {leader.image ? (
                      <div className="w-full h-64 relative">
                        <PageEditableImage
                          path={`leadership.leaders.${index}.image`}
                          fallback={leader.image}
                          alt={leader.name}
                          className="w-full h-64 object-cover object-top"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-64 bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center">
                        <Users className="w-16 h-16 text-white/50" />
                      </div>
                    )}
                    <div className="p-6">
                      <PageEditable
                        path={`leadership.leaders.${index}.name`}
                        as="h3"
                        className="text-xl font-bold text-foreground"
                        fallback={leader.name}
                      />
                      <PageEditable
                        path={`leadership.leaders.${index}.role`}
                        as="p"
                        className="text-primary font-medium mb-3"
                        fallback={leader.role}
                      />
                      <PageEditable
                        path={`leadership.leaders.${index}.bio`}
                        as="p"
                        multiline
                        className="text-muted-foreground text-sm"
                        fallback={leader.bio}
                      />
                    </div>
                  </motion.div>
                </FadeIn>
              )}
          />
          </div>
        </section>
      )}

      {/* ========== LONG-STANDING MEMBERS ========== */}
      {content.members?.longStandingMembers?.length > 0 && (
        <section id="members" className="py-20 scroll-mt-24">
          <div className="max-w-6xl mx-auto px-6">
            <FadeIn>
              <div className="text-center mb-16">
                <PageEditable path="members.eyebrow" as="span" className="text-primary font-bold tracking-wider text-sm mb-4 block" fallback="OUR COMMUNITY" />
                <PageEditable
                  path="members.title"
                  as="h2"
                  className="text-3xl md:text-4xl font-bold text-foreground mb-4"
                  fallback={content.members?.title || "Our Family"}
                />
                <PageEditable
                  path="members.description"
                  as="p"
                  multiline
                  className="text-lg text-muted-foreground max-w-2xl mx-auto"
                  fallback={content.members?.description || ""}
                />
              </div>
            </FadeIn>

            <EditableList
              path="members.longStandingMembers"
              fallback={content.members.longStandingMembers}
              emptyItem={() => ({ name: 'New Member', role: '', bio: '', years: '' })}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
              renderItem={(member, index) => (
                <FadeIn key={index} delay={index * 0.05}>
                  <div className="bg-muted/30 rounded-xl p-5 hover:bg-primary/10 transition-colors h-full">
                    <div className="w-14 h-14 mb-4 rounded-full bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{member.name?.charAt(0)}</span>
                    </div>
                    <PageEditable
                      path={`members.longStandingMembers.${index}.name`}
                      as="h4"
                      className="font-bold text-foreground mb-1"
                      fallback={member.name}
                    />
                    <PageEditable
                      path={`members.longStandingMembers.${index}.role`}
                      as="p"
                      className="text-sm text-primary font-medium mb-2"
                      fallback={member.role || ''}
                    />
                    <PageEditable
                      path={`members.longStandingMembers.${index}.bio`}
                      as="p"
                      multiline
                      className="text-xs text-muted-foreground mb-2"
                      fallback={member.bio || ''}
                    />
                    <PageEditable
                      path={`members.longStandingMembers.${index}.years`}
                      as="p"
                      className="text-xs text-yellow-600 font-semibold"
                      fallback={member.years || ''}
                    />
                  </div>
                </FadeIn>
              )}
            />
          </div>
        </section>
      )}

      {/* ========== LEGACY LINK ========== */}
      <section className="py-20 bg-card border-y border-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <PageEditable
              path="legacy.title"
              as="h2"
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              fallback="Discover the Legacy"
            />
            <PageEditable
              path="legacy.description"
              as="p"
              multiline
              className="text-xl text-white/90 mb-8"
              fallback="Learn about Mestre Curtis Pierre, the visionary behind Escola de Samba Casa Samba and Ambassador of Afro-Brazilian Culture."
            />
            <EditableLink
              path="legacy.ctaLink"
              fallbackHref="/legacy"
              fallbackLabel="The Legacy of Mestre Curtis Pierre"
              className="inline-flex items-center gap-3 px-10 py-5 bg-primary text-primary-foreground font-bold text-lg rounded-full hover:bg-yellow-400 hover:text-black transition-all shadow-lg"
            >
              <Star className="w-6 h-6" />
            </EditableLink>
          </FadeIn>
        </div>
      </section>

      {/* ========== CONTACT ========== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <PageEditable path="contact.eyebrow" as="span" className="text-primary font-bold tracking-wider text-sm mb-4 block" fallback="GET IN TOUCH" />
            <PageEditable
              path="contact.title"
              as="h2"
              className="text-3xl md:text-4xl font-bold text-foreground mb-8"
              fallback={content.contact?.title || "Connect With Us"}
            />
            
            <div className="flex flex-wrap justify-center gap-6">
              {content.contact?.email && (
                <a 
                  href={`mailto:${content.contact.email}`}
                  className="flex items-center gap-3 px-6 py-4 bg-muted/40 rounded-xl hover:bg-primary/15 transition-colors"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  <PageEditable path="contact.email" className="font-medium text-foreground" fallback={content.contact.email} />
                </a>
              )}
              {content.contact?.location && (
                <div className="flex items-center gap-3 px-6 py-4 bg-muted/40 rounded-xl">
                  <MapPin className="w-5 h-5 text-primary" />
                  <PageEditable path="contact.location" className="font-medium text-foreground" fallback={content.contact.location} />
                </div>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

export default About;
